import React from 'react';
import PermissionsContainer from '../../../components/PermissionsContainer';
import { ORDER_INVOICING_FIELDS_COMPONENT_ID } from '../useCanEditInvoicingFields';

/**
 * Frame + registration anchor for `order-invoicing-fields`.
 *
 * Exists for the same reason `OrderLcFieldGroup` does — a component permission only
 * reaches the admin Permissions UI by being mounted inside a `PermissionsContainer`,
 * which draws the permissions-mode frame and self-registers the id. It makes no
 * editability decision: `AddlInfoView.canEditLc` does that, ORing this component with
 * `order-lc-fields` for the keys in ORDER_INVOICING_FIELD_KEYS.
 *
 * `defaultDisabled` (not defaultHidden) is what keeps that true. An un-granted component
 * resolves to View (1), so the frame renders its child either way and the LC who has
 * always been able to type here still can. Hidden would have unmounted the field.
 *
 * MOUNTED ON MASTER PRO ONLY, though the component governs three fields — and that is
 * deliberate, not an oversight. `PermissionsContainer` publishes its resolved level
 * through `PermissionAccessProvider`, and the permission-aware primitives
 * (`useIsDisabledByPermission`) disable themselves at level 1. Master Pro is a plain
 * `TextInput`, which is not permission-aware, so a frame there is inert. Consignee is a
 * `LocationTypeAhead` and Rate Zone a `SelectInput` — both ARE permission-aware, so
 * framing them would disable them for an LC who holds `order-lc-fields` and not this
 * one, turning a widening gate into a narrowing one. One anchor registers the component
 * for the whole set; the frame outlining a single field is the cost of that.
 *
 * Templates pass through undecorated: there Master Pro is the Master Template No., owned
 * by `template-edit-fields`, and framing it as an invoicing component would say the
 * wrong thing about who may edit it.
 */
const InvoicingFieldGroup: React.FC<{ isTemplate: boolean; children: React.ReactNode }> = ({
  isTemplate,
  children,
}) =>
  isTemplate ? (
    <>{children}</>
  ) : (
    <PermissionsContainer
      componentId={ORDER_INVOICING_FIELDS_COMPONENT_ID}
      componentName="Order Fields — Invoicing"
      defaultDisabled
    >
      {children}
    </PermissionsContainer>
  );

export default InvoicingFieldGroup;
